/**
 * CompletionStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * Étape finale: Récapitulatif et complétion
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Receipt, LayoutDashboard, Sparkles, PartyPopper } from 'lucide-react';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';

interface CompletionStepProps {
  onComplete: () => void;
}

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  isLoading?: boolean;
  className?: string;
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-semibold transition-all duration-300 w-full
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const CompletionStep: React.FC<CompletionStepProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const { status, complete } = useOnboarding();
  const navigate = useNavigate();
  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async () => {
    try {
      setIsCompleting(true);
      await complete();
      logger.log('🎉 Onboarding terminé!');
    } catch (err) {
      logger.error('❌ Erreur complétion:', err);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleCreateQuote = () => {
    navigate('/quotes/new');
  };

  if (!status) return null;

  const { progress } = status;

  const achievements = [
    { label: t('onboarding.completion.companyCreated'), completed: progress?.companyCreated },
    { label: t('onboarding.completion.legalMentions'), completed: progress?.legalMentionsAdded },
    { label: t('onboarding.completion.logoAdded'), completed: progress?.logoUploaded },
    { label: t('onboarding.completion.clientAdded'), completed: progress?.firstClientAdded },
    { label: t('onboarding.completion.productAdded'), completed: progress?.firstProductAdded },
    { label: t('onboarding.completion.quoteCreated'), completed: progress?.firstQuoteCreated },
  ];

  const completedCount = achievements.filter(a => a.completed).length;

  return (
    <div>
      {/* Header avec célébration */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-lime-400 to-lime-500 shadow-lg shadow-lime-500/30 mb-6 animate-bounce">
          <PartyPopper className="w-10 h-10 text-teal-700" />
        </div>
        <h1 className="text-4xl font-bold text-white mb-3">
          {t('onboarding.completion.congratulations')}
        </h1>
        <p className="text-white/60 text-lg">
          {completedCount > 1
            ? t('onboarding.completion.stepsCompletedPlural', { count: completedCount })
            : t('onboarding.completion.stepsCompleted', { count: completedCount })
          }
        </p>
      </div>

      {/* Récapitulatif */}
      <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 mb-8 border border-white/20 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
            <Sparkles size={20} className="text-lime-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">
            {t('onboarding.completion.whatYouConfigured')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 p-4 rounded-2xl transition-all ${
                achievement.completed
                  ? 'bg-lime-500/10 border border-lime-400/30'
                  : 'bg-white/5 border border-white/10'
              }`}
            >
              {achievement.completed ? (
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-teal-800" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-xl border-2 border-white/20 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-white/20" />
                </div>
              )}
              <span className={achievement.completed ? 'text-white font-medium' : 'text-white/40'}>
                {achievement.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        {progress?.firstClientAdded && progress?.firstProductAdded && !progress?.firstQuoteCreated && (
          <GlassButton onClick={handleCreateQuote}>
            <Receipt className="w-5 h-5" />
            {t('onboarding.completion.createFirstQuote')}
          </GlassButton>
        )}

        <GlassButton
          onClick={handleComplete}
          disabled={isCompleting}
          isLoading={isCompleting}
          variant={progress?.firstQuoteCreated ? 'primary' : 'secondary'}
        >
          {!isCompleting && <LayoutDashboard className="w-5 h-5" />}
          {isCompleting ? t('onboarding.completion.finalizing') : t('onboarding.completion.accessDashboard')}
        </GlassButton>
      </div>

      <p className="text-center text-sm text-white/40 mt-8">
        {t('onboarding.completion.completeFromSettings')}
      </p>
    </div>
  );
};
